// FE-32 — modelo de dominio de finanzas: clientes, tarifas, ingresos y
// resultado (ADR-030, backend BE-73).
//
// Hereda las cuatro ideas de `types/expense.ts` (el importe es TEXTO,
// `occurredAt` no es `createdAt`, el viaje se deriva, el soporte se versiona) y
// añade tres propias:
//
//  1. **La cuenta vive en el core.** El frontend NO calcula un flete: pide
//     `POST /tariffs/{id}/quote` y pinta lo que vuelve. Reimplementar la
//     fórmula aquí es exactamente la duplicación que el cuaderno del dueño
//     tenía en cada fila (ADR-030 §2).
//  2. **El importe que MANDA es `amount`, lo que se cobró.** La tarifa propone
//     (`computedAmount`); si la persona cobra otra cosa tiene que decir por qué
//     (`overrideReason`), y esa razón entra a la bitácora en el propio registro.
//  3. **Una tarifa es UNA VIGENCIA.** Subir precios es crear otra con
//     `validFrom` nuevo y cerrar la anterior con `validTo` (inclusivo, el último
//     día en que rige); los precios de una tarifa existente no se editan. El
//     ingreso guarda la FOTO de la tarifa con la que se calculó
//     (`tariffSnapshot`), así que la lista de precios puede cambiar sin
//     reescribir lo cobrado.

export interface Customer {
  id: string;
  name: string;
  active: boolean;
  createdAt: string;
  updatedAt?: string;
}

/** Las tres formas del contrato (ADR-030 §1). */
export type TariffForm = 'FLAT' | 'PER_UNIT' | 'FIXED_PLUS_PER_UNIT';

export const TARIFF_FORMS: { id: TariffForm; label: string; ayuda: string }[] = [
  { id: 'FLAT', label: 'Precio por servicio', ayuda: 'Un valor fijo por viaje o servicio, sin cantidad.' },
  { id: 'PER_UNIT', label: 'Por unidad', ayuda: 'Precio × cantidad (kg, km, horas…).' },
  { id: 'FIXED_PLUS_PER_UNIT', label: 'Fijo + por unidad', ayuda: 'Un cargo fijo más precio × cantidad. La del transporte por peso.' },
];

export interface Tariff {
  id: string;
  customerId: string;
  customerName: string;
  /** Ruta, destino o nombre del servicio. */
  name: string;
  form: TariffForm;
  /** Unidad de la cantidad (`kg`, `km`…). Ausente en `FLAT`. */
  unit?: string;
  /** `Money` como texto. */
  fixedAmount?: string;
  /** Hasta cuatro decimales, como texto. */
  unitPrice?: string;
  /** Porcentaje (8 = 8 %) que se descuenta de la CANTIDAD antes de multiplicar. */
  discountPct: number;
  /** Piso del total propuesto, como texto. */
  minimumAmount?: string;
  /** Cantidad (en `unit`) por debajo de la cual se cobra el mínimo FIJO, sin fórmula. Ausente = el mínimo es solo piso. */
  minimumThresholdQuantity?: string;
  /** Solo vehículos de esa clase; ausente = todos. */
  vehicleClass?: string;
  currency: string;
  /** Fecha `YYYY-MM-DD`. */
  validFrom: string;
  /** Último día en que rige (inclusivo). Ausente = sigue vigente. */
  validTo?: string;
  active: boolean;
  createdAt: string;
  updatedAt?: string;
}

/** Lo que la tarifa propone para una cantidad. Es la respuesta de `/quote`, sin tocar. */
export interface TariffQuote {
  tariffId: string;
  quantity?: string;
  /** Cantidad tras el descuento: lo que se multiplica. */
  chargeableQuantity?: string;
  /** Antes del mínimo. */
  baseAmount?: string;
  /** Lo que la app propone cobrar. */
  computedAmount: string;
  minimumApplied: boolean;
}

export interface RevenueAttachment {
  id: string;
  contentType: string;
  sizeBytes: number;
  integrityHash: string;
  uploadedBy: string;
  uploadedAt: string;
}

export type RevenueOrigin = 'MANUAL' | 'TRIP_START';

export interface Revenue {
  id: string;
  customerId: string;
  customerName: string;
  tariffId?: string;
  tariffName?: string;
  /** La FOTO de la tarifa en el momento del cálculo. Las claves son las de `Tariff`. */
  tariffSnapshot?: Record<string, unknown>;
  /** Cantidad en la unidad de la tarifa, como texto con cuatro decimales. */
  quantity?: string;
  /** Lo que la tarifa propuso. Ausente sin tarifa. */
  computedAmount?: string;
  /** Lo que se cobra. MANDA. */
  amount: string;
  currency: string;
  /** Cuándo se PRESTÓ el servicio: decide viaje, mes y vigencia. */
  occurredAt: string;
  vehicleId?: string;
  vehiclePlate?: string;
  subUnitId?: string;
  driverId?: string;
  driverName?: string;
  tripId?: string;
  /** Número de tiquete, remesa o factura. Texto libre. */
  reference?: string;
  notes?: string;
  /**
   * `MANUAL` = alguien lo registró; `TRIP_START` = lo creó el core al iniciar
   * el viaje (ADR-032). La marca «del viaje» de la UI sale de aquí, para que
   * nadie lo registre dos veces a mano.
   */
  origin: RevenueOrigin;
  registeredBy: string;
  createdAt: string;
  updatedAt?: string;
  /** El soporte VIGENTE (tiquete de báscula, remesa). */
  attachment?: RevenueAttachment;
}

export type RevenueAction = 'REGISTERED' | 'CORRECTED';

export interface RevenueChange {
  field: string;
  from: string;
  to: string;
}

/**
 * Un paso de la bitácora. En `REGISTERED`, `changes` trae `amount` solo cuando
 * el importe cobrado difirió del propuesto (`from` = propuesto, `to` =
 * cobrado) y `reason` explica por qué.
 */
export interface RevenueTransition {
  action: RevenueAction;
  changes: RevenueChange[];
  actor: string;
  /** Nombre legible de quien lo hizo (#111). Opcional: el core puede no resolverlo. */
  actorName?: string;
  reason?: string;
  recordedAt: string;
}

export type RevenueUnsettable = 'tariffId' | 'quantity' | 'vehicleId' | 'driverId' | 'tripId' | 'reference' | 'notes';

export type FinanceGroupBy = 'period' | 'vehicle' | 'subunit' | 'category' | 'customer';

/** Las cifras de una fila o del total. Todo `Money` como texto; `result` puede ser negativo. */
export interface FinanceFigures {
  income: string;
  /** Lo que se resta. Por vehículo excluye `expensesOperation` (la nómina no es de un camión). */
  expenses: string;
  expensesVehicle: string;
  expensesOperation: string;
  result: string;
  /** Suma de cantidades de los ingresos (kg del periodo). Ausente si ninguno la tiene. */
  quantity?: string;
  revenueCount: number;
  expenseCount: number;
}

export interface FinanceSummaryRow extends FinanceFigures {
  /** `YYYY-MM`, o el id de la entidad; vacío para «sin vehículo / sin sub-unidad». */
  key: string;
  label: string;
  /** Solo en `groupBy=category`. */
  scope?: 'VEHICLE' | 'OPERATION';
}

export interface FinanceSummary {
  from: string;
  to: string;
  groupBy: FinanceGroupBy;
  totals: FinanceFigures;
  rows: FinanceSummaryRow[];
}
